import { useMemo } from 'react';
import Iconify from '@syntra/ui/components/iconify';

import { paths } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { FeatureFlag, useFeatureFlag } from 'src/auth/feature-flags';

import type { NavProps } from 'src/components/nav-section';

// ----------------------------------------------------------------------

// const icon = (name: string) => (
//   <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
//   // OR
//   // <Iconify icon="fluent:mail-24-filled" />
//   // https://icon-sets.iconify.design/solar/
//   // https://www.streamlinehq.com/icons
// );

// const ICONS = {
//   calendar: icon('ic_calendar'),
//   dashboard: icon('ic_dashboard'),
// };
// ----------------------------------------------------------------------
export function useNavData() {
  const { t } = useTranslate('nav');
  const reportBuilderEnabled = useFeatureFlag(FeatureFlag.ReportBuilder);
  const customFieldsEnabled = useFeatureFlag(FeatureFlag.CustomFields);
  const companyDashboardEnabled = useFeatureFlag(FeatureFlag.CompanyDashboard);
  const companyPartnerEnabled = useFeatureFlag(FeatureFlag.CompanyPartners);
  const companyProjectEnabled = useFeatureFlag(FeatureFlag.CompanyProjects);
  const companyProjectActionEnabled = useFeatureFlag(FeatureFlag.CompanyProjectActions);
  const companyProjectCategoryEnabled = useFeatureFlag(FeatureFlag.CompanyProjectCategory);
  const companyRoleManagementEnabled = useFeatureFlag(FeatureFlag.CompanyRoleManagement);
  const companyUserManagementEnabled = useFeatureFlag(FeatureFlag.CompanyUserManagement);
  const supportTicketEnabled = useFeatureFlag(FeatureFlag.SupportTicket);
  const helpCenterEnabled = useFeatureFlag(FeatureFlag.HelpCenter);

  const data = useMemo<NavProps['data']>(
    () => [
      {
        subheader: t('page_management'),
        roles: ['authenticated'],
        items: [
          {
            id: 'dashboard_user_management',
            title: t('users'),
            disabled: false,
            path: paths.dashboard.users.index,
            icon: <Iconify icon="solar:user-id-linear" width={24} height={24} />,
            children: [
              ...(companyUserManagementEnabled
                ? [
                    {
                      title: t('user_management'),
                      path: paths.dashboard.users.index,
                      id: 'dashboard_users_view',
                      icon: (
                        <Iconify icon="solar:users-group-rounded-linear" width={24} height={24} />
                      ),
                      permissions: [{ resource: 'users' as const, action: 'read' as const }],
                    },
                  ]
                : []),
              ...(companyRoleManagementEnabled
                ? [
                    {
                      title: t('role_management'),
                      path: paths.dashboard.roles.index,
                      id: 'dashboard_roles_view',
                      icon: <Iconify icon="solar:shield-user-linear" width={24} height={24} />,
                      permissions: [
                        {
                          resource: 'role-based-access-controls' as const,
                          action: 'read' as const,
                        },
                      ],
                    },
                  ]
                : []),
            ],
          },
        ],
      },
      // OVERVIEW
      // ----------------------------------------------------------------------
      {
        subheader: t('company_management'),

        items: [
          ...(companyDashboardEnabled
            ? [
                {
                  title: t('company_dashboard'),
                  path: paths.dashboard.company_managment_dashboard.index,
                  id: 'company_managment_dashboard_view',
                  icon: <Iconify icon="solar:diagram-up-linear" width={24} height={24} />,
                },
              ]
            : []),
          ...(companyPartnerEnabled
            ? [
                {
                  title: t('company_partners'),
                  path: paths.dashboard.company_partner.index,
                  id: 'dashboard_company_partner_view',
                  icon: <Iconify icon="solar:buildings-3-linear" width={24} height={24} />,
                  permissions: [{ resource: 'company-partners' as const, action: 'read' as const }],
                },
              ]
            : []),
          ...(companyProjectEnabled
            ? [
                {
                  title: t('company_projects'),
                  path: paths.dashboard.company_project.index,
                  id: 'dashboard_company_project_view',
                  icon: <Iconify icon="solar:book-bookmark-linear" width={24} height={24} />,
                  permissions: [{ resource: 'company-projects' as const, action: 'read' as const }],
                },
              ]
            : []),
          ...(companyProjectActionEnabled
            ? [
                {
                  title: t('company_project_actions'),
                  path: paths.dashboard.company_project_action.index,
                  id: 'dashboard_company_project_action_view',
                  icon: <Iconify icon="solar:inbox-archive-linear" width={24} height={24} />,
                  permissions: [
                    { resource: 'company-project-actions' as const, action: 'read' as const },
                  ],
                },
              ]
            : []),
          ...(companyProjectCategoryEnabled
            ? [
                {
                  title: t('company_project_category'),
                  path: paths.dashboard.company_project_category.index,
                  id: 'dashboard_company_project_category_view',
                  icon: <Iconify icon="solar:inbox-archive-linear" width={24} height={24} />,
                  permissions: [
                    { resource: 'company-project-categories' as const, action: 'read' as const },
                  ],
                },
              ]
            : []),
          ...(reportBuilderEnabled
            ? [
                {
                  title: t('reports'),
                  path: paths.dashboard.reports.index,
                  id: 'dashboard_reports_view',
                  icon: <Iconify icon="solar:chart-2-bold-duotone" width={24} height={24} />,
                },
              ]
            : []),
        ],
      },
      // SUPPORT
      // ----------------------------------------------------------------------
      {
        subheader: t('support'),

        items: [
          ...(supportTicketEnabled
            ? [
                {
                  title: t('support_tickets'),
                  path: paths.support.tickets.index,
                  id: 'support_tickets_view',
                  icon: <Iconify icon="solar:chat-round-call-linear" width={24} height={24} />,
                },
              ]
            : []),
          ...(helpCenterEnabled
            ? [
                {
                  title: t('help_center'),
                  path: paths.dashboard.help_center.index,
                  id: 'dashboard_help_center_view',
                  icon: <Iconify icon="solar:play-circle-linear" width={24} height={24} />,
                },
              ]
            : []),
          ...(customFieldsEnabled
            ? [
                {
                  title: t('custom_fields'),
                  path: paths.dashboard.custom_fields.index,
                  id: 'dashboard_custom_fields_view',
                  icon: <Iconify icon="solar:settings-linear" width={24} height={24} />,
                  permissions: [{ resource: 'custom-fields' as const, action: 'read' as const }],
                },
              ]
            : []),
        ],
      },
    ],
    [
      t,
      reportBuilderEnabled,
      customFieldsEnabled,
      companyDashboardEnabled,
      companyPartnerEnabled,
      companyProjectEnabled,
      companyProjectActionEnabled,
      companyProjectCategoryEnabled,
      companyRoleManagementEnabled,
      companyUserManagementEnabled,
      supportTicketEnabled,
      helpCenterEnabled,
    ]
  );

  return data;
}
